import { totpVerifySchema, type TotpVerifyInput } from '@cnss/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { messageErreurApi } from '@/lib/api-error';
import { useAuth } from '@/lib/auth-context';

interface TwoFactorStepProps {
  challengeToken: string;
  onSuccess: () => void;
}

/** Deuxième étape de connexion : code TOTP à 6 chiffres (obligatoire pour les rôles sensibles). */
export function TwoFactorStep({ challengeToken, onSuccess }: TwoFactorStepProps) {
  const { completeTwoFactor } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TotpVerifyInput>({
    resolver: zodResolver(totpVerifySchema),
    defaultValues: { challengeToken },
  });

  const onSubmit = async (values: TotpVerifyInput) => {
    setServerError(null);
    try {
      await completeTwoFactor(values.challengeToken, values.code);
      onSuccess();
    } catch (error) {
      setServerError(messageErreurApi(error, 'Code incorrect ou expiré.'));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <KeyRound className="h-6 w-6" aria-hidden />
          </div>
          <CardTitle className="text-xl">Vérification en deux étapes</CardTitle>
          <CardDescription>
            Saisissez le code à 6 chiffres de votre application d&apos;authentification
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <input type="hidden" {...register('challengeToken')} />
            <div className="space-y-2">
              <Label htmlFor="code">Code de vérification</Label>
              {/* Page dédiée à une seule action : le focus automatique sur le champ de
                  code est ici un gain d'accessibilité clavier, pas une gêne. */}
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                className="text-center text-lg tracking-[0.5em]"
                {...register('code')}
              />
              {errors.code && <p className="text-sm text-destructive">{errors.code.message}</p>}
            </div>
            {serverError && (
              <p
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {serverError}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Vérification…' : 'Valider'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
